<p-panel [toggleable]="true" [collapsed]="collapsed()" (collapsedChange)="collapsed.set($event)">
  <ng-template pTemplate="header">
    <div class="row-start-center gap-3">
      <span class="font-semibold">{{ 'Cost_Analysis_Pool_Control_Title' | translateWF: 'Cost Pools' }}</span>
      <span class="inline-flex items-center rounded-full bg-[var(--p-surface-100)] px-2 py-0.5 text-xs font-medium text-[var(--p-text-muted-color)] tabular-nums">
        {{ year() }}-{{ month() < 10 ? '0' + month() : month() }}
      </span>
      @if (allReady()) {
        <span class="inline-flex items-center gap-1 rounded-full bg-[var(--p-green-100)] px-2 py-0.5 text-xs font-medium text-[var(--p-green-800)]">
          <i class="pi pi-check-circle"></i>
          {{ 'Cost_Analysis_All_Pools_Ready' | translateWF: 'All pools ready' }}
        </span>
      }
    </div>
  </ng-template>

  <div class="col-sm">
    <p-panel header="Parameters" data-tone="surface">
      <ng-template pTemplate="icons">
        <!-- Action row -->
        <p-button
          severity="secondary"
          icon="pi pi-refresh"
          [label]="'Cost_Analysis_Refresh_Status' | translateWF: 'Refresh Status'"
          [loading]="loadingStatus()"
          [disabled]="running() || runningMonthlyClose()"
          (click)="loadStatus()"
        />
        <p-button
          icon="pi pi-bolt"
          [label]="'Cost_Analysis_Calculate' | translateWF: 'Calculate'"
          [loading]="runningMonthlyClose()"
          [disabled]="running() || loadingStatus() || !allReady()"
          (click)="runMonthlyClose()"
        />
      </ng-template>

      <!-- Inputs row -->
      <p-card>
        <div class="grid-12">
          <div class="span-12-4">
            <label class="block text-xs font-medium uppercase tracking-wider text-[var(--p-text-muted-color)] mb-1">
              {{ 'Cost_Analysis_Exchange_Rate_HNL_USD' | translateWF: 'Exchange Rate (HNL / USD)' }}
            </label>
            <p-inputNumber
              [formControl]="exchangeRateControl"
              [minFractionDigits]="4"
              [maxFractionDigits]="4"
              [min]="0"
              styleClass="w-full"
              [placeholder]="exchangeRateLoading() ? ('Loading' | translateWF: 'Loading…') : '—'"
            />
            @if (exchangeRateEffectiveAt(); as eff) {
              <p class="mt-1 text-xs text-[var(--p-text-muted-color)]">
                <span class="tabular-nums">{{ eff | date: 'dd/MM/yyyy' }}</span>
              </p>
            } @else if (!exchangeRateLoading() && exchangeRateControl.value === null) {
              <p class="mt-1 text-xs text-[var(--p-red-600)]">
                {{ 'Cost_Analysis_Rate_Unavailable' | translateWF: 'No HNL → USD rate available.' }}
              </p>
            }
          </div>
          <div class="span-12-4">
            <label class="block text-xs font-medium uppercase tracking-wider text-[var(--p-text-muted-color)] mb-1">
              {{ 'Cost_Analysis_Exchange_Rate_EUR_USD' | translateWF: 'Exchange Rate (EUR / USD)' }}
            </label>
            <p-inputNumber [formControl]="exchangeRateEurUsdControl" [minFractionDigits]="2" [maxFractionDigits]="6" [min]="0" styleClass="w-full" />
          </div>
        </div>
      </p-card>

      <ng-template pTemplate="footer">
        <span class="text-xs text-[var(--p-text-muted-color)]">
          @if (lastCheckedAt()) {
            <span>
              {{ 'Cost_Analysis_Last_Check' | translateWF: 'Last check' }}:
              <span class="tabular-nums">{{ lastCheckedAt() | date: 'HH:mm' }}</span>
            </span>
          }
        </span>
      </ng-template>
    </p-panel>

    <!-- Results table -->
    <p-panel header="Results" data-tone="surface">
      <p-card data-variant="compact">
        <p-table [value]="rows()" [rowTrackBy]="trackByName" [tableStyle]="{ 'min-width': '720px' }">
          <ng-template pTemplate="header">
            <tr>
              <th class="text-xs uppercase tracking-wider font-semibold">
                {{ 'Cost_Analysis_Col_Pool' | translateWF: 'Pool' }}
              </th>
              <th class="text-xs uppercase tracking-wider font-semibold">
                {{ 'Cost_Analysis_Col_Status' | translateWF: 'Status' }}
              </th>
              <th class="text-xs uppercase tracking-wider font-semibold">
                {{ 'Cost_Analysis_Col_LastComputed' | translateWF: 'Last Computed' }}
              </th>
              <th class="text-xs uppercase tracking-wider font-semibold">
                {{ 'Cost_Analysis_Col_ComputedBy' | translateWF: 'Computed By' }}
              </th>
              <th class="text-xs uppercase tracking-wider font-semibold">
                {{ 'Cost_Analysis_Col_LastError' | translateWF: 'Last Error' }}
              </th>
              <th class="text-xs uppercase tracking-wider font-semibold text-right">
                {{ 'Cost_Analysis_Col_Action' | translateWF: 'Action' }}
              </th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td class="font-medium text-[var(--p-text-color)]">{{ row.name }}</td>
              <td>
                <span [class]="chipClasses(row.status)">
                  <i [class]="chipIcon(row.status)" style="font-size: 0.75rem"></i>
                  {{ chipLabel(row.status) }}
                </span>
              </td>
              <td class="tabular-nums text-xs text-[var(--p-text-muted-color)]">
                {{ row.computedAt ? (row.computedAt | date: 'dd/MM/yyyy HH:mm') : '—' }}
              </td>
              <td class="text-xs text-[var(--p-text-muted-color)]">{{ row.computedBy ?? '—' }}</td>
              <td>
                @if (row.lastError) {
                  <span class="text-xs text-[var(--p-red-700)]">{{ row.lastError }}</span>
                } @else {
                  <span class="text-[var(--p-text-muted-color)]">—</span>
                }
              </td>
              <td class="text-right">
                <p-button
                  size="small"
                  severity="secondary"
                  [icon]="row.status === 2 ? 'pi pi-replay' : 'pi pi-play'"
                  [label]="row.status === 2 ? ('Cost_Analysis_Recompute' | translateWF: 'Recompute') : ('Cost_Analysis_Compute' | translateWF: 'Compute')"
                  [loading]="row.status === 'requesting' || row.status === 1"
                  [disabled]="running() || runningMonthlyClose()"
                  (click)="computePool(row.name)"
                />
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="6" class="text-center text-sm text-[var(--p-text-muted-color)] py-4">
                {{ 'Cost_Analysis_No_Rows' | translateWF: 'No rows.' }}
              </td>
            </tr>
          </ng-template>
        </p-table>
      </p-card>
    </p-panel>
  </div>
</p-panel>
